import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Inbox, Mail, Phone, Building2, FileText, Calendar, Download, Trash2,
  CheckCircle2, Clock, MessageCircle, Filter, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_OPTIONS = [
  { value: 'novo',         label: 'Novo',         cls: 'bg-teal-500/20 text-teal-300 border-teal-500/40' },
  { value: 'em_andamento', label: 'Em andamento', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  { value: 'respondido',   label: 'Respondido',   cls: 'bg-blue-500/20 text-blue-300 border-blue-500/40' },
  { value: 'fechado',      label: 'Fechado',      cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  { value: 'descartado',   label: 'Descartado',   cls: 'bg-slate-600/40 text-slate-300 border-slate-500/40' },
];
const statusMap = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s]));

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const BudgetRequests = () => {
  const { user, getAuthHeaders } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);

  const isAdmin = user?.role === 'dono';

  const load = async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `${API}/budget-requests?status=${statusFilter}` : `${API}/budget-requests`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setItems(res.data);
    } catch {
      toast.error('Erro ao carregar solicitações');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) load(); }, [statusFilter, isAdmin]); // eslint-disable-line

  const counts = useMemo(() => {
    const c = { total: items.length };
    STATUS_OPTIONS.forEach(s => { c[s.value] = items.filter(i => i.status === s.value).length; });
    return c;
  }, [items]);

  if (user && !isAdmin) return <Navigate to="/dashboard" replace />;

  const handleStatusChange = async (id, newStatus) => {
    try {
      const fd = new FormData();
      fd.append('status', newStatus);
      await axios.put(`${API}/budget-requests/${id}`, fd, { headers: getAuthHeaders() });
      toast.success('Status atualizado');
      setItems(prev => prev.map(i => i.id === id ? { ...i, status: newStatus } : i));
      if (selected?.id === id) setSelected({ ...selected, status: newStatus });
    } catch { toast.error('Erro ao atualizar status'); }
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      const fd = new FormData();
      fd.append('notes', selected.notes || '');
      await axios.put(`${API}/budget-requests/${selected.id}`, fd, { headers: getAuthHeaders() });
      toast.success('Notas salvas');
      setItems(prev => prev.map(i => i.id === selected.id ? { ...i, notes: selected.notes } : i));
    } catch { toast.error('Erro ao salvar notas'); }
    finally { setSavingNotes(false); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm('Excluir esta solicitação permanentemente?');
    if (!ok) return;
    try {
      await axios.delete(`${API}/budget-requests/${id}`, { headers: getAuthHeaders() });
      toast.success('Solicitação excluída');
      setItems(prev => prev.filter(i => i.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleDownload = async (id, name) => {
    try {
      const res = await axios.get(`${API}/budget-requests/${id}/file`, { headers: getAuthHeaders(), responseType: 'arraybuffer' });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name || 'arquivo';
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 300);
    } catch { toast.error('Erro ao baixar arquivo'); }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="budget-requests-page">
        <div className="flex items-center gap-3">
          <Inbox className="h-7 w-7 text-teal-400" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Solicitações de Orçamento</h1>
            <p className="text-sm text-slate-400">Pedidos enviados pela landing pública.</p>
          </div>
        </div>

        {/* Status cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <button onClick={() => setStatusFilter('')}
            className={`text-left p-3 rounded-lg border transition-colors ${statusFilter === '' ? 'bg-slate-800 border-teal-500/40' : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'}`}
            data-testid="filter-all">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Total</p>
            <p className="text-xl font-bold text-white mt-1">{counts.total}</p>
          </button>
          {STATUS_OPTIONS.map(s => (
            <button key={s.value} onClick={() => setStatusFilter(s.value)}
              className={`text-left p-3 rounded-lg border transition-colors ${statusFilter === s.value ? `border-current ${s.cls.split(' ')[1]}` : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'}`}
              data-testid={`filter-${s.value}`}>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{s.label}</p>
              <p className="text-xl font-bold text-white mt-1">{counts[s.value] || 0}</p>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-teal-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-12 text-center">
            <Inbox className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">{statusFilter ? 'Nenhuma solicitação com este status.' : 'Nenhuma solicitação recebida ainda.'}</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-4">
            {/* List */}
            <div className={`lg:col-span-${selected ? 5 : 12} space-y-2`}>
              {items.map(r => (
                <button
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className={`w-full text-left bg-slate-800/40 hover:bg-slate-800/70 border rounded-xl p-4 transition-all ${selected?.id === r.id ? 'border-teal-500/50 ring-1 ring-teal-500/30' : 'border-slate-700'}`}
                  data-testid={`budget-item-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white truncate">{r.name}</p>
                      <p className="text-xs text-slate-400 truncate">{r.email}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${statusMap[r.status]?.cls || 'bg-slate-700 text-slate-300'}`}>
                      {statusMap[r.status]?.label || r.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> {r.project_type}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(r.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Detail */}
            {selected && (
              <div className="lg:col-span-7 bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-5 sticky top-4 self-start" data-testid="budget-detail">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xl font-bold text-white">{selected.name}</h2>
                    <p className="text-xs text-slate-400 mt-1">Recebida em {formatDate(selected.created_at)}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-white" data-testid="close-detail">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <InfoRow icon={Mail} label="Email" value={<a href={`mailto:${selected.email}`} className="text-teal-400 hover:underline">{selected.email}</a>} />
                  <InfoRow icon={Phone} label="Telefone" value={selected.phone || '—'} />
                  <InfoRow icon={Building2} label="Empresa" value={selected.company || '—'} />
                  <InfoRow icon={FileText} label="Tipo" value={selected.project_type} />
                  <InfoRow icon={Clock} label="Duração estimada" value={selected.estimated_duration || '—'} />
                  <InfoRow icon={Calendar} label="Prazo desejado" value={selected.desired_deadline || '—'} />
                  <InfoRow icon={CheckCircle2} label="Orçamento estimado" value={selected.estimated_budget || '—'} />
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Briefing</p>
                  <div className="bg-slate-900/60 border-l-4 border-teal-500/60 rounded-r-lg p-4 text-sm text-slate-200 whitespace-pre-wrap" data-testid="detail-briefing">
                    {selected.briefing}
                  </div>
                </div>

                {selected.file_name && (
                  <div className="flex items-center justify-between bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2">
                    <span className="flex items-center gap-2 text-sm text-slate-200 truncate">
                      <FileText className="h-4 w-4 text-teal-400 shrink-0" />
                      {selected.file_name}
                    </span>
                    <button
                      onClick={() => handleDownload(selected.id, selected.file_name)}
                      className="inline-flex items-center gap-1 text-xs text-teal-400 hover:text-teal-300"
                      data-testid="download-budget-file"
                    >
                      <Download className="h-3.5 w-3.5" /> Baixar
                    </button>
                  </div>
                )}

                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Status</p>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map(s => (
                      <button
                        key={s.value}
                        onClick={() => handleStatusChange(selected.id, s.value)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selected.status === s.value ? s.cls : 'bg-slate-800/50 text-slate-400 border-slate-700 hover:border-slate-500'}`}
                        data-testid={`status-${s.value}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2 inline-flex items-center gap-1.5">
                    <MessageCircle className="h-3.5 w-3.5" /> Notas internas
                  </p>
                  <textarea
                    value={selected.notes || ''}
                    onChange={e => setSelected({ ...selected, notes: e.target.value })}
                    rows={3}
                    className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-teal-500"
                    placeholder="Observações internas, retornos do cliente, valor enviado..."
                    data-testid="notes-textarea"
                  />
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="mt-2 text-xs bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 px-3 py-1.5 rounded"
                    data-testid="save-notes"
                  >
                    {savingNotes ? 'Salvando...' : 'Salvar notas'}
                  </button>
                </div>

                <div className="border-t border-slate-700 pt-4 flex justify-end">
                  <button
                    onClick={() => handleDelete(selected.id)}
                    className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300"
                    data-testid="delete-budget"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Excluir solicitação
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="bg-slate-900/40 rounded-lg p-3">
    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 inline-flex items-center gap-1">
      <Icon className="h-3 w-3" /> {label}
    </p>
    <p className="text-sm text-white break-words">{value}</p>
  </div>
);

export default BudgetRequests;
